import { useState, useEffect } from 'react';
import { AdminNavigation } from '../../components/AdminNavigation';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import { creditsReconciliationService } from '../../lib/creditsReconciliationService';
import {
  Coins,
  Search,
  RefreshCcw,
  Download,
  Check,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
} from 'lucide-react';

interface UserCredits {
  user_id: string;
  full_name: string;
  email: string;
  balance: number;
  total_earned: number;
  total_spent: number;
}

export function CreditsManagement() {
  const { profile } = useAuthStore();
  const [users, setUsers] = useState<UserCredits[]>([]);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [stats, setStats] = useState({
    totalCreditsIssued: 0,
    totalCreditsSpent: 0,
    totalBalance: 0,
    avgBalance: 0,
  });

  useEffect(() => {
    loadCreditsData();
  }, []);

  const loadCreditsData = async () => {
    setLoading(true);
    try {
      const { data: creditsData, error } = await supabase
        .from('credits')
        .select(`
          user_id,
          balance,
          total_earned,
          total_spent,
          profiles!inner(full_name, email)
        `)
        .order('balance', { ascending: false });

      if (error) throw error;

      const formattedUsers: UserCredits[] = (creditsData || []).map((item: any) => ({
        user_id: item.user_id,
        full_name: item.profiles.full_name,
        email: item.profiles.email,
        balance: item.balance,
        total_earned: item.total_earned,
        total_spent: item.total_spent,
      }));

      setUsers(formattedUsers);

      const totalIssued = formattedUsers.reduce((sum, u) => sum + u.total_earned, 0);
      const totalSpent = formattedUsers.reduce((sum, u) => sum + u.total_spent, 0);
      const totalBal = formattedUsers.reduce((sum, u) => sum + u.balance, 0);

      setStats({
        totalCreditsIssued: totalIssued,
        totalCreditsSpent: totalSpent,
        totalBalance: totalBal,
        avgBalance: formattedUsers.length > 0 ? Math.round(totalBal / formattedUsers.length) : 0,
      });
    } catch (error) {
      console.error('Error loading credits data:', error);
      setMessage({ type: 'error', text: 'Failed to load credits data' });
    } finally {
      setLoading(false);
    }
  };

  const handleReconcile = async () => {
    if (!profile) return;

    setReconciling(true);
    setMessage(null);

    try {
      const result = await creditsReconciliationService.fixAllUserCredits(profile.id);
      setMessage({
        type: 'success',
        text: `Reconciliation complete! Fixed ${result.usersFixed} users and awarded ${result.totalCreditsAwarded} credits.`,
      });
      await loadCreditsData();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Reconciliation failed' });
    } finally {
      setReconciling(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const report = await creditsReconciliationService.generateDetailedReport();
      const blob = new Blob([report], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `credits-reconciliation-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: 'Report downloaded successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to export report' });
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <AdminNavigation />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 ml-64">
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold flex items-center gap-3">
                  <Coins className="w-10 h-10" />
                  Credits Management
                </h1>
                <p className="text-blue-100 mt-2">Monitor and manage platform credits</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleExportReport}
                  className="px-6 py-3 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition flex items-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Export Report
                </button>
                <button
                  onClick={handleReconcile}
                  disabled={reconciling}
                  className="px-6 py-3 bg-orange-500 hover:bg-orange-600 rounded-lg font-semibold transition flex items-center gap-2 disabled:opacity-50"
                >
                  <RefreshCcw className={`w-5 h-5 ${reconciling ? 'animate-spin' : ''}`} />
                  {reconciling ? 'Reconciling...' : 'Reconcile All'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {message && (
            <div
              className={`mb-6 p-4 rounded-xl border-2 flex items-start gap-3 ${
                message.type === 'success'
                  ? 'bg-green-50 border-green-300 text-green-900'
                  : 'bg-red-50 border-red-300 text-red-900'
              }`}
            >
              {message.type === 'success' ? (
                <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              )}
              <p className="font-semibold">{message.text}</p>
            </div>
          )}

          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-6 h-6 text-green-600" />
                <h3 className="font-bold text-gray-700">Total Issued</h3>
              </div>
              <div className="text-3xl font-bold text-gray-900">{stats.totalCreditsIssued.toLocaleString()}</div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-2">
                <TrendingDown className="w-6 h-6 text-red-600" />
                <h3 className="font-bold text-gray-700">Total Spent</h3>
              </div>
              <div className="text-3xl font-bold text-gray-900">{stats.totalCreditsSpent.toLocaleString()}</div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-6 h-6 text-blue-600" />
                <h3 className="font-bold text-gray-700">Total Balance</h3>
              </div>
              <div className="text-3xl font-bold text-gray-900">{stats.totalBalance.toLocaleString()}</div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-6 h-6 text-cyan-600" />
                <h3 className="font-bold text-gray-700">Avg Balance</h3>
              </div>
              <div className="text-3xl font-bold text-gray-900">{stats.avgBalance.toLocaleString()}</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-6">
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                />
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 px-4 font-bold text-gray-700">User</th>
                      <th className="text-right py-3 px-4 font-bold text-gray-700">Balance</th>
                      <th className="text-right py-3 px-4 font-bold text-gray-700">Total Earned</th>
                      <th className="text-right py-3 px-4 font-bold text-gray-700">Total Spent</th>
                      <th className="text-right py-3 px-4 font-bold text-gray-700">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.user_id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                        <td className="py-3 px-4">
                          <div>
                            <div className="font-semibold text-gray-900">{user.full_name}</div>
                            <div className="text-sm text-gray-600">{user.email}</div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="font-bold text-gray-900">{user.balance.toLocaleString()}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-green-600 font-semibold">+{user.total_earned.toLocaleString()}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-red-600 font-semibold">-{user.total_spent.toLocaleString()}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span
                            className={`font-bold ${
                              user.total_earned - user.total_spent >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {(user.total_earned - user.total_spent).toLocaleString()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
